import {
  Type,
  Link as LinkIcon,
  FileText,
  Image as ImageIcon,
  PenTool,
  Eraser,
  Highlighter,
  Shapes,
  Undo,
  Redo,
  ChevronDown,
  Plus,
  Upload,
  Pen,
  ArrowRight,
  Minus,
  Circle,
  Square,
  Check,
  Edit3,
  MousePointer2,
  Stamp,
  StickyNote,
} from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import type { Textbox } from "fabric";

// Helper for transparent background
const removeBackground = (imageSrc: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(imageSrc);
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      // Simple white/light background removal
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        // If pixel is close to white, make it transparent
        if (r > 230 && g > 230 && b > 230) {
          data[i + 3] = 0;
        }
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => resolve(imageSrc);
  });
};

interface ToolbarProps {
  activeTool: string;
  setActiveTool: (str: string) => void;
  selectedColor: string;
  setSelectedColor: (str: string) => void;
  fontSize: string;
  setFontSize: (str: string) => void;
  fontFamily?: string;
  setFontFamily?: (str: string) => void;
  handleAddText?: () => void;
  activateDrawMode: () => void;
  penColor?: string;
  setPenColor?: (color: string) => void;
  penSize?: number;
  setPenSize?: (size: number) => void;
  updateActiveText: (updates: Partial<Textbox>) => void;
  toggleTextStyle: (str: string) => void;
  activateHighlightMode: () => void;
  handleAddShape: (type: string) => void;
  handleAddImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  addImageToCanvas: (url: string, options?: any) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onFinishDrawing?: () => void;
  handleAddField: (type: string) => void;
  activateLinkMode: () => void;
  onEnterEditMode?: () => void;
  stamps?: string[];
}

export const Toolbar = ({
  activeTool,
  setActiveTool,
  selectedColor,
  setSelectedColor,
  fontSize,
  setFontSize,
  fontFamily,
  setFontFamily,
  handleAddText,
  activateDrawMode,

  updateActiveText,
  toggleTextStyle,
  activateHighlightMode,
  handleAddShape,

  addImageToCanvas,
  onUndo,
  onRedo,
  onFinishDrawing,
  handleAddField,
  activateLinkMode,
  setPenColor,
  setPenSize,
  penColor,
  penSize,
  onEnterEditMode,
  stamps,
}: ToolbarProps) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // History State
  const [recentImages, setRecentImages] = useState<string[]>([]);
  const [recentSignatures, setRecentSignatures] = useState<string[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [signatureModal, setSignatureModal] = useState<{
    isOpen: boolean;
    original: string | null;
    transparent: string | null;
  }>({ isOpen: false, original: null, transparent: null });

  useEffect(() => {
    const savedImages = localStorage.getItem("recentImages");
    if (savedImages) setRecentImages(JSON.parse(savedImages));
    const savedSignatures = localStorage.getItem("recentSignatures");
    if (savedSignatures) setRecentSignatures(JSON.parse(savedSignatures));
  }, []);

  const addToRecent = (
    url: string,
    list: string[],
    setList: (l: string[]) => void,
    key: string,
  ) => {
    const newList = [url, ...list.filter((u) => u !== url)].slice(0, 5);
    setList(newList);
    localStorage.setItem(key, JSON.stringify(newList));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (f) => {
      const data = f.target?.result as string;
      if (data) {
        addImageToCanvas(data);
        addToRecent(data, recentImages, setRecentImages, "recentImages");
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
    setActiveDropdown(null);
  };

  const handleSignatureSelection = (selected: string) => {
    addImageToCanvas(selected, { scaleX: 0.3, scaleY: 0.3 });
    if (signatureModal.original) {
      addToRecent(
        signatureModal.original,
        recentSignatures,
        setRecentSignatures,
        "recentSignatures",
      );
    }
    if (
      signatureModal.transparent &&
      signatureModal.transparent !== signatureModal.original
    ) {
      addToRecent(
        signatureModal.transparent,
        recentSignatures,
        setRecentSignatures,
        "recentSignatures",
      );
    }
    setSignatureModal({ isOpen: false, original: null, transparent: null });
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (f) => {
      const data = f.target?.result as string;
      if (data) {
        const transparent = await removeBackground(data);
        setSignatureModal({
          isOpen: true,
          original: data,
          transparent: transparent,
        });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
    setActiveDropdown(null);
  };

  const toggleDropdown = (e: React.MouseEvent, id: string) => {
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 8, left: rect.left });
      setActiveDropdown(id);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest(".toolbar-dropdown")) return;
      setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const colors = ["#000000", "#EF4444", "#3B82F6", "#10B981", "#F59E0B"];
  const fontOptions = [
    "Helvetica",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
  ];

  const ToolButton = ({
    active,
    onClick,
    icon: Icon,
    label,
    hasDropdown,
    className,
  }: {
    active?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    icon: any;
    label: string;
    hasDropdown?: boolean;
    className?: string;
  }) => (
    <button
      onClick={onClick}
      className={`
        relative px-3 py-2 rounded-xl flex items-center gap-2 transition-all duration-200 group
        ${
          active
            ? "bg-blue-50 text-blue-600 ring-1 ring-blue-200 shadow-sm"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }
        ${className}
      `}
    >
      <Icon
        size={18}
        className={
          active ? "text-blue-600" : "text-slate-500 group-hover:text-slate-700"
        }
      />
      <span className="text-sm font-medium">{label}</span>
      {hasDropdown && (
        <ChevronDown
          size={14}
          className="text-slate-400 group-hover:text-slate-600"
        />
      )}
    </button>
  );

  const Divider = () => <div className="w-px h-6 bg-slate-200 mx-1" />;

  return (
    <div className="w-full bg-white border-b border-gray-200 z-50 relative shadow-sm">
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-2 overflow-x-auto no-scrollbar">
        {/* LEFT: ESSENTIAL TOOLS */}
        <div className="flex items-center gap-1">
          {/* Hand/Select */}
          <button
            onClick={() => setActiveTool("select")}
            className={`p-2 rounded-lg transition-colors ${
              activeTool === "select"
                ? "bg-blue-100 text-blue-600"
                : "text-slate-500 hover:bg-slate-100"
            }`}
            title="Select"
          >
            <MousePointer2 size={18} />
          </button>

          <Divider />

          {/* TEXT */}
          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeTool === "text"}
              onClick={(e) => {
                setActiveTool("text");
                handleAddText?.();
                toggleDropdown(e, "text");
              }}
              icon={Type}
              label="Text"
              hasDropdown
            />
            {activeDropdown === "text" && (
              <div
                className="fixed w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 z-[100]"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Text Properties
                </div>
                <div className="flex items-center justify-between gap-2">
                  <select
                    value={fontFamily?.split(" ")[0] || "Arial"}
                    onChange={(e) => setFontFamily?.(e.target.value)}
                    className="flex-1 h-9 px-2 text-sm border border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                  >
                    {fontOptions.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <select
                    value={fontSize}
                    onChange={(e) => setFontSize?.(e.target.value)}
                    className="w-20 h-9 px-2 text-sm border border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                  >
                    {[8, 10, 12, 14, 16, 18, 24, 36, 48, 72].map((s) => (
                      <option key={s} value={s}>
                        {s}px
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex gap-1 bg-gray-50 p-1 rounded-lg">
                    <button
                      onClick={() => toggleTextStyle("fontWeight")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="font-bold text-sm">B</span>
                    </button>
                    <button
                      onClick={() => toggleTextStyle("fontStyle")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="italic text-sm">I</span>
                    </button>
                    <button
                      onClick={() => toggleTextStyle("underline")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="underline text-sm">U</span>
                    </button>
                  </div>
                  <div className="flex gap-1">
                    {colors.slice(0, 5).map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          updateActiveText({ fill: c });
                          setSelectedColor(c);
                        }}
                        className={`w-6 h-6 rounded-full border border-gray-200 ${selectedColor === c ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* EDIT TEXT */}
          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeTool === "edit"}
              onClick={(e) => {
                setActiveTool("edit");
                onEnterEditMode?.();
                toggleDropdown(e, "edit");
              }}
              icon={Edit3}
              label="Edit Text"
              hasDropdown
            />
            {activeDropdown === "edit" && (
              <div
                className="fixed w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 z-[100]"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Edit Text Properties
                </div>
                <div className="flex items-center justify-between gap-2">
                  <select
                    value={fontFamily?.split(" ")[0] || "Arial"}
                    onChange={(e) => {
                      const font = e.target.value;
                      setFontFamily?.(font);
                      updateActiveText?.({ fontFamily: font });
                    }}
                    className="flex-1 h-9 px-2 text-sm border border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                  >
                    {fontOptions.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <select
                    value={fontSize}
                    onChange={(e) => {
                      const size = e.target.value;
                      setFontSize?.(size);
                      updateActiveText?.({ fontSize: Number(size) });
                    }}
                    className="w-20 h-9 px-2 text-sm border border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                  >
                    {[8, 10, 12, 14, 16, 18, 24, 36, 48, 72].map((s) => (
                      <option key={s} value={s}>
                        {s}px
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex gap-1 bg-gray-50 p-1 rounded-lg">
                    <button
                      onClick={() => toggleTextStyle("fontWeight")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="font-bold text-sm">B</span>
                    </button>
                    <button
                      onClick={() => toggleTextStyle("fontStyle")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="italic text-sm">I</span>
                    </button>
                    <button
                      onClick={() => toggleTextStyle("underline")}
                      className="p-1.5 hover:bg-white rounded shadow-sm transition-all"
                    >
                      <span className="underline text-sm">U</span>
                    </button>
                  </div>
                  <div className="flex gap-1">
                    {colors.slice(0, 5).map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          updateActiveText({ fill: c });
                          setSelectedColor(c);
                        }}
                        className={`w-6 h-6 rounded-full border border-gray-200 ${selectedColor === c ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Divider />

          {/* INSERT GROUP */}
          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeDropdown === "image"}
              onClick={(e) => toggleDropdown(e, "image")}
              icon={ImageIcon}
              label="Image"
              hasDropdown
            />
            {activeDropdown === "image" && (
              <div
                className="fixed w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-[100] animate-in fade-in slide-in-from-top-2"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                    <Plus size={16} />
                  </div>
                  <div>
                    <div className="font-medium">New Image</div>
                    <div className="text-xs text-gray-400">
                      Upload from computer
                    </div>
                  </div>
                </button>
                {recentImages.length > 0 && (
                  <>
                    <div className="my-2 border-t border-gray-100" />
                    <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Recent
                    </div>
                    <div className="grid grid-cols-4 gap-2 px-2">
                      {recentImages.map((src, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            addImageToCanvas(src);
                            setActiveDropdown(null);
                          }}
                          className="aspect-square rounded-lg border border-gray-200 overflow-hidden hover:border-blue-500 hover:ring-2 ring-blue-100 transition-all relative group bg-gray-50"
                        >
                          <img
                            src={src}
                            className="w-full h-full object-contain"
                            alt="recent"
                          />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
          </div>

          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeDropdown === "sign"}
              onClick={(e) => toggleDropdown(e, "sign")}
              icon={PenTool}
              label="Sign"
              hasDropdown
            />
            {activeDropdown === "sign" && (
              <div
                className="fixed w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-[100] animate-in fade-in slide-in-from-top-2"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={() => signatureInputRef.current?.click()}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <Upload size={16} />
                  </div>
                  <div>
                    <div className="font-medium">Upload Signature</div>
                    <div className="text-xs text-gray-400">
                      Auto-removes background
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setActiveTool("draw");
                    activateDrawMode();
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                    <Pen size={16} />
                  </div>
                  <div>
                    <div className="font-medium">Draw Signature</div>
                    <div className="text-xs text-gray-400">
                      Use mouse or touch
                    </div>
                  </div>
                </button>
                {recentSignatures.length > 0 && (
                  <>
                    <div className="my-2 border-t border-gray-100" />
                    <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Recent
                    </div>
                    <div className="grid grid-cols-2 gap-2 px-2">
                      {recentSignatures.map((src, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            addImageToCanvas(src, { scaleX: 0.3, scaleY: 0.3 });
                            setActiveDropdown(null);
                          }}
                          className="h-12 rounded-lg border border-gray-200 overflow-hidden hover:border-blue-500 hover:ring-2 ring-blue-100 transition-all bg-white p-1"
                        >
                          <img
                            src={src}
                            className="w-full h-full object-contain"
                            alt="signature"
                          />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <input
              type="file"
              ref={signatureInputRef}
              onChange={handleSignatureUpload}
              accept="image/*"
              className="hidden"
            />
          </div>

          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeDropdown === "stamp"}
              onClick={(e) => toggleDropdown(e, "stamp")}
              icon={Stamp}
              label="Stamp"
              hasDropdown
            />
            {activeDropdown === "stamp" && (
              <div
                className="fixed w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 z-[100]"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Select Stamp
                </div>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {stamps && stamps.length > 0 ? (
                    stamps.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          addImageToCanvas(s, { scaleX: 0.5, scaleY: 0.5 });
                          setActiveDropdown(null);
                        }}
                        className="aspect-square border border-gray-200 rounded-lg p-1 hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center bg-gray-50"
                        title={`Stamp ${i + 1}`}
                      >
                        <img
                          src={s}
                          alt={`Stamp ${i}`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </button>
                    ))
                  ) : (
                    <div className="col-span-4 text-center text-xs text-gray-400 py-4 italic">
                      No stamps available
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 text-center border-t border-gray-100 pt-2">
                  Click to add stamp to canvas
                </div>
              </div>
            )}
          </div>

          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeDropdown === "shapes"}
              onClick={(e) => toggleDropdown(e, "shapes")}
              icon={Shapes}
              label="Shapes"
              hasDropdown
            />
            {activeDropdown === "shapes" && (
              <div
                className="fixed w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-[100] animate-in fade-in slide-in-from-top-2"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={() => {
                    handleAddShape("rect");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Square size={16} /> Rectangle
                </button>
                <button
                  onClick={() => {
                    handleAddShape("circle");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Circle size={16} /> Ellipse
                </button>
                <button
                  onClick={() => {
                    handleAddShape("line");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Minus size={16} /> Line
                </button>
                <button
                  onClick={() => {
                    handleAddShape("arrow");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <ArrowRight size={16} /> Arrow
                </button>
              </div>
            )}
          </div>

          <div className="relative toolbar-dropdown">
            <ToolButton
              active={activeDropdown === "forms"}
              onClick={(e) => toggleDropdown(e, "forms")}
              icon={FileText}
              label="Forms"
              hasDropdown
            />
            {activeDropdown === "forms" && (
              <div
                className="fixed w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-[100] animate-in fade-in slide-in-from-top-2"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={() => {
                    handleAddField("text");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Type size={16} /> Text Field
                </button>
                <button
                  onClick={() => {
                    handleAddField("checkbox");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Check size={16} /> Checkbox
                </button>
                <button
                  onClick={() => {
                    handleAddField("radio");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Circle size={16} /> Radio Button
                </button>
              </div>
            )}
          </div>

          <ToolButton
            active={activeTool === "link"}
            onClick={() => {
              setActiveTool("link");
              activateLinkMode();
            }}
            icon={LinkIcon}
            label="Link"
          />

          <Divider />

          {/* ANNOTATE */}
          <div className="relative toolbar-dropdown">
            <ToolButton
              active={
                activeDropdown === "annotate" || activeTool === "highlight"
              }
              onClick={(e) => toggleDropdown(e, "annotate")}
              icon={Highlighter}
              label="Annotate"
              hasDropdown
            />
            {activeDropdown === "annotate" && (
              <div
                className="fixed w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-[100] animate-in fade-in slide-in-from-top-2"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={() => {
                    setActiveTool("highlight");
                    activateHighlightMode();
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Highlighter size={16} /> Highlight Text
                </button>
                <button
                  onClick={() => {
                    handleAddShape("note");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <StickyNote size={16} /> Sticky Note
                </button>
                <button
                  onClick={() => {
                    handleAddShape("stamp");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Stamp size={16} /> Stamp
                </button>
                <button
                  onClick={() => {
                    handleAddShape("whiteout");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Eraser size={16} /> Whiteout
                </button>
                <button
                  onClick={() => {
                    handleAddShape("redact");
                    setActiveDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 text-sm text-gray-700"
                >
                  <Square size={16} fill="black" /> Redact
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: ACTIONS & HISTORY */}
        <div className="flex items-center gap-2">
          {/* DRAWING CONTROLS */}
          {activeTool === "draw" && (
            <div className="flex items-center gap-2 mr-2 px-3 py-1.5 bg-slate-100 rounded-full border border-slate-200 animate-in fade-in slide-in-from-left-2">
              <span className="text-xs font-bold text-slate-500 uppercase">
                Pen
              </span>
              <div className="flex gap-1">
                {colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setPenColor?.(c)}
                    className={`w-4 h-4 rounded-full border border-gray-200 transition-transform hover:scale-110 ${penColor === c ? "ring-2 ring-blue-500 ring-offset-1 scale-110" : ""}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="w-px h-4 bg-slate-300 mx-1" />
              <input
                type="range"
                min="1"
                max="10"
                value={penSize}
                onChange={(e) => setPenSize?.(parseInt(e.target.value))}
                className="w-16 h-1 bg-slate-300 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <button
                onClick={onFinishDrawing}
                className="ml-2 p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                title="Done Drawing"
              >
                <Check size={14} />
              </button>
            </div>
          )}

          <div className="flex items-center bg-slate-50 p-1 rounded-xl border border-slate-200">
            <button
              onClick={onUndo}
              className="p-2 hover:bg-white hover:shadow-sm rounded-lg text-slate-500 hover:text-slate-900 transition-all"
              title="Undo"
            >
              <Undo size={18} />
            </button>
            <button
              onClick={onRedo}
              className="p-2 hover:bg-white hover:shadow-sm rounded-lg text-slate-500 hover:text-slate-900 transition-all"
              title="Redo"
            >
              <Redo size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {signatureModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Choose Signature Version
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              We created a transparent version of your signature. Please select
              the one you want to use.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Original */}
              <div
                onClick={() =>
                  signatureModal.original &&
                  handleSignatureSelection(signatureModal.original)
                }
                className="cursor-pointer group relative rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all p-4 flex flex-col items-center gap-3"
              >
                <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={signatureModal.original || ""}
                    alt="Original"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <span className="font-medium text-gray-700 group-hover:text-blue-700">
                  Original
                </span>
              </div>

              {/* Transparent */}
              <div
                onClick={() =>
                  signatureModal.transparent &&
                  handleSignatureSelection(signatureModal.transparent)
                }
                className="cursor-pointer group relative rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all p-4 flex flex-col items-center gap-3"
              >
                <div className="w-full aspect-video bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWCEAvjyMGLQLADA66aa/KB01AAAAABJRU5ErkJggg==')] rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={signatureModal.transparent || ""}
                    alt="Transparent"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <span className="font-medium text-gray-700 group-hover:text-blue-700">
                  Transparent
                </span>
              </div>
            </div>

            <button
              onClick={() =>
                setSignatureModal({
                  isOpen: false,
                  original: null,
                  transparent: null,
                })
              }
              className="w-full py-2.5 text-gray-500 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
